import React, { ChangeEvent, useEffect, useState } from "react";
import YhInput from "../../../../ui/YhInput";
import YhButton from "../../../../ui/YhButton";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../../../ui/YhTable";
import {
  isTruthy,
  openErrorNotification,
} from "../../../../helpers/methods";
import customErrorHandler from "../../../../utils/customErrorHandler";
import notifiers from "../../../../global/constants/NotificationConstants";
import history from "../../../../utils/history";
import urls from "../../../../global/constants/UrlConstants";
import { getRoleById } from "../Role/AddRole/roleService";

interface CustomProps {
  isAddTeamPage: boolean;
  isDirectAddTeam: boolean;
}

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const TeamTabContent = (props: CustomProps) => {
  const [search, setSearch] = useState("");
  const [userDataSource, setUserDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [assignOpen, setAssignOpen] = useState(false);
  const [modalData, setModalData] = useState<any[]>([]);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.isAddTeamPage) {
      history.push(urls.ADDROLEPAGE);
    }
  }, [props.isAddTeamPage]);

  const init = async () => {
    setLoading(true);
    try {
      const response = await getRoleById();
      if (response?.data?.length > 0) {
        customErrorHandler(response);
        setUserDataSource(response.data);
      } else {
        setUserDataSource([]);
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = userDataSource.filter((row: any) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return String(row.role_name || "")
      .toLowerCase()
      .includes(q);
  });

  const pagedRows = filteredRows.slice(
    paginationModel.page * paginationModel.pageSize,
    paginationModel.page * paginationModel.pageSize + paginationModel.pageSize
  );

  const columns: YhTableColumn[] = [
    {
      headerName: "Role",
      field: "role_name",
      minWidth: 200,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm font-semibold capitalize text-[#111827]">
          {params.row?.role_name || "—"}
        </span>
      ),
    },
    {
      headerName: "Type",
      field: "isOrganization",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <span className="inline-flex rounded-full bg-[#EEF0FF] px-2.5 py-1 text-xs font-semibold text-[#4338CA]">
          {params.row?.isOrganization === 1 ? "Organization" : "Team"}
        </span>
      ),
    },
    {
      headerName: "Assigned Users",
      field: "users",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          className="text-sm font-semibold text-[#A02448] hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            setModalData(params.row?.users || []);
            setAssignOpen(true);
          }}
        >
          {params.row?.users?.length || 0}
        </button>
      ),
    },
    {
      headerName: "Action",
      field: "action",
      minWidth: 100,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          className="text-sm font-semibold text-[#A02448] hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            history.push(
              urls.EDITROLEPAGE.replace(":id", params.row?._id)
            );
          }}
        >
          Edit
        </button>
      ),
    },
  ];

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="min-w-[240px] flex-1 sm:max-w-[420px]">
          <YhInput
            placeholder="Search roles…"
            value={search}
            type="search"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearch(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            startAdornment={<SearchSvg />}
            containerClassName="w-full max-w-[420px]"
          />
        </div>
        {!props.isDirectAddTeam ? (
          <YhButton
            variant="primary"
            size="sm"
            onClick={() => history.push(urls.ADDROLEPAGE)}
          >
            + Add Role
          </YhButton>
        ) : null}
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <YhTable
          columns={columns}
          rows={pagedRows}
          getRowId={(row) => row._id}
          loading={loading}
          emptyMessage="No roles found"
          className="h-full"
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          totalRows={filteredRows.length}
          pageSizeOptions={[10, 25, 50]}
          onPageChange={(p) =>
            setPaginationModel((prev) => ({ ...prev, page: p }))
          }
          onPageSizeChange={(s) =>
            setPaginationModel({ pageSize: s, page: 0 })
          }
        />
      </div>

      {assignOpen ? (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-bold text-[#111827]">
                Assigned Users
              </h3>
              <button
                type="button"
                onClick={() => {
                  setAssignOpen(false);
                  setModalData([]);
                }}
                className="text-[#6B7280] hover:text-[#111827]"
              >
                ✕
              </button>
            </div>
            <div className="max-h-60 space-y-2 overflow-auto">
              {modalData?.length ? (
                modalData.map((user: any, index: number) => (
                  <div
                    key={index}
                    className="rounded-lg border border-[#EEF0F5] px-3 py-2 text-sm text-[#374151]"
                  >
                    {user.name}
                  </div>
                ))
              ) : (
                <p className="text-sm text-[#6B7280]">No users assigned</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TeamTabContent;
